import { useNavigate } from 'react-router-dom'
import { useOrders } from '../hooks/useOrders'
import { StatusBadge } from '../components/common/Badge'
import { KANBAN_COLUNAS } from '../data/kanban'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Plus, Package, TrendingUp, Clock, CheckCircle2, Wrench } from 'lucide-react'
import { formatCurrency } from '../utils/fmt'

function formatDate(d) {
  if (!d) return '—'
  try { return format(new Date(d + 'T12:00:00'), "dd/MM/yy", { locale: ptBR }) } catch { return d }
}

// Status considerados "em produção" — exclui Entregue e Pós-venda.
const STATUS_EM_PRODUCAO = ['CADASTRADO', 'FABRICANDO', 'EXPEDICAO', 'ROTA', 'AJUSTE', 'A_FABRICAR']

// Verdadeiro se a data (YYYY-MM-DD) cair no mês corrente.
function isMesCorrente(dataStr) {
  if (!dataStr) return false
  const hoje = new Date()
  const [y, m] = String(dataStr).split('-').map(Number)
  return y === hoje.getFullYear() && (m - 1) === hoje.getMonth()
}

export function Dashboard() {
  const navigate          = useNavigate()
  const { pedidos: pedidosTodos } = useOrders()
  // Vendas normais — exclui AJ e pedidos filhos divididos
  // (mãe carrega o valor; filhos são só logística)
  const pedidos           = pedidosTodos.filter(p => !p.tipoAjuste && !p.tipoFilho)
  // Ajustes — só os pedidos fantasmas (AJ-...)
  const ajustes           = pedidosTodos.filter(p =>  p.tipoAjuste)

  // Pedidos do mês corrente — filtrados pela data da venda.
  const pedidosDoMes      = pedidos.filter(p => isMesCorrente(p.dataVenda))

  const fechados          = pedidosDoMes.length
  const entregues         = pedidosDoMes.filter(p => p.status === 'ENTREGUE').length
  const emProducao        = pedidosDoMes.filter(p => STATUS_EM_PRODUCAO.includes(p.status)).length
  const faturamento       = pedidosDoMes.reduce((s, p) => s + (p.valorTotal || 0), 0)

  // Ajustes — usa dataAbertura pra filtrar do mês (AJ não tem dataVenda)
  function isoNoMes(iso) {
    if (!iso) return false
    const d = new Date(iso)
    const h = new Date()
    return d.getFullYear() === h.getFullYear() && d.getMonth() === h.getMonth()
  }
  const ajustesDoMes      = ajustes.filter(p => isoNoMes(p.dataAbertura))
  const ajustesFeitos     = ajustesDoMes.filter(p => p.status === 'ENTREGUE').length
  const ajustesAbertos    = ajustes.filter(p => p.status !== 'ENTREGUE').length

  const recentes          = pedidos.slice(0, 6)

  const porStatus = KANBAN_COLUNAS.map(col => ({
    ...col,
    count: pedidos.filter(p => p.status === col.id).length,
  }))

  const nomeDoMes = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })

  const stats = [
    { label: `Fechados em ${nomeDoMes}`,    value: fechados,                  icon: Package,      color: 'bg-slate-100 text-slate-600' },
    { label: `Em produção em ${nomeDoMes}`, value: emProducao,                icon: Clock,        color: 'bg-amber-100 text-amber-700' },
    { label: `Entregues em ${nomeDoMes}`,   value: entregues,                 icon: CheckCircle2, color: 'bg-pink-100 text-pink-700'   },
    { label: `Faturamento ${nomeDoMes}`,    value: formatCurrency(faturamento), icon: TrendingUp, color: 'bg-[#FFF9C2] text-[#CC8800]' },
    {
      label: `Ajustes em ${nomeDoMes}`,
      value: ajustesFeitos,
      icon:  Wrench,
      color: 'bg-amber-50 text-amber-700',
      sub:   ajustesAbertos > 0 ? `${ajustesAbertos} em aberto` : 'nenhum em aberto',
    },
  ]

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-sm text-slate-400 mt-0.5">Visão geral dos pedidos</p>
        </div>
        <button onClick={() => navigate('/pedido/novo')} className="btn-primary">
          <Plus size={15} />
          Novo pedido
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {stats.map(s => (
          <div key={s.label} className="card p-5">
            <div className={`w-9 h-9 rounded-xl ${s.color} flex items-center justify-center mb-3`}>
              <s.icon size={18} />
            </div>
            <p className="text-2xl font-bold text-slate-800">{s.value}</p>
            <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
            {s.sub && (
              <p className="text-[10px] text-slate-400 mt-1 font-medium">{s.sub}</p>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pedidos recentes */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">Pedidos recentes</h2>
            <button onClick={() => navigate('/pedidos')} className="text-xs text-[#E6A000] hover:underline">
              Ver todos
            </button>
          </div>

          {recentes.length === 0 ? (
            <div className="card p-10 text-center">
              <Package className="mx-auto text-slate-200 mb-3" size={32} />
              <p className="text-sm text-slate-400">Nenhum pedido ainda</p>
              <button onClick={() => navigate('/pedido/novo')} className="btn-primary mx-auto mt-4 text-sm">
                <Plus size={15} /> Criar pedido
              </button>
            </div>
          ) : (
            <div className="card overflow-hidden">
              {recentes.map((p, i) => (
                <div
                  key={p.id}
                  onClick={() => navigate(`/pedidos/${p.id}`)}
                  className={`flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors
                    ${i < recentes.length - 1 ? 'border-b border-slate-50' : ''}
                  `}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{p.cliente?.nome}</p>
                    <p className="text-xs text-slate-400 font-mono">{p.numero} · {formatDate(p.prazo)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-slate-700">{formatCurrency(p.valorTotal)}</p>
                    <StatusBadge status={p.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Kanban resumo */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">Por status</h2>
            <button onClick={() => navigate('/kanban')} className="text-xs text-[#E6A000] hover:underline">
              Ver kanban
            </button>
          </div>
          <div className="card divide-y divide-slate-50">
            {porStatus.map(col => (
              <div key={col.id} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-slate-700">{col.label}</span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${col.color.split(' ').slice(0, 2).join(' ')}`}>
                  {col.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
